import { Tooltip } from "react-tooltip";
import { ITradeSettingsStore } from "@/store/tradeSettingsStore";
import { resetCPOverrideFields } from "@/store/tradeSettingsStoreACTIONS";
import {
  ICPSettingsOverride,
  IToggleButton,
  IToggleButtonToReturnBoolean,
  IToggleCounterpartiesButton,
} from "../../helper/tradeSettingsHelper/tradeSettingsInterfaces";
import { refreshArrow } from "@/components/icons/SVGIcons";
import { resolveSettingForCP } from "./SelectPanelHelper";
import {
  isSameOverrideFieldValue,
  isTradeLevelFieldPending,
} from "./CPSettingsGridHelper";
import {
  ICEBERG_AT_MAX_TOOLTIP,
  getEffectiveIcebergFloorForCP,
} from "./CPSettingsGridIcebergHelpers";
import { ICPGridSettingConfig } from "./CPSettingsGridConstants";
import { SettingDropdown } from "./SettingDropdown";
import { SettingNumericInput } from "./SettingNumericInput";
import { SINGLE_CP_COVER_TOOLTIP } from "../../helper/tradeSettingsHelper/coverSettingsRule";

interface ICPOverrideRowsProps {
  esid: string;
  activeCounterparties: IToggleCounterpartiesButton[];
  visibleCPColumns: ICPGridSettingConfig[];
  thisTradeSettings: ITradeSettingsStore;
  editBaseline: Record<string, ICPSettingsOverride> | undefined;
  tradeLevelBaseline: Partial<ICPSettingsOverride> | undefined;
  knownCpCodes: string[] | undefined;
  fontSizeClass: string;
  bankWidth: string;
  removeWidth: string;
  gap: string;
  // LQC-1704: lock the per-CP Advise Cover dropdown when only a single CP is
  // active (the override grid can still render in that case via a leftover
  // pending/saved border).
  lockCoverBySingleCp?: boolean;
}

export function CPOverrideRows({
  esid,
  activeCounterparties,
  visibleCPColumns,
  thisTradeSettings,
  editBaseline,
  tradeLevelBaseline,
  knownCpCodes,
  fontSizeClass,
  bankWidth,
  removeWidth,
  gap,
  lockCoverBySingleCp,
}: ICPOverrideRowsProps) {
  return (
    <>
      {activeCounterparties.map((cp: IToggleCounterpartiesButton) => {
        const cpOverride =
          thisTradeSettings.cpSettingsOverrides[cp.code] || {};
        const hasAnyOverride = Object.keys(cpOverride).length > 0;
        const cpBaseline = editBaseline?.[cp.code];

        return (
          <div
            key={cp.code}
            className={`mb-1 flex flex-wrap items-center ${gap}`}
            data-testid={`BankOverrideRow-${cp.code}`}
            id={`BankOverrideRow-${cp.code}`}
          >
            <div className={`${bankWidth} font-medium ${fontSizeClass}`}>
              {cp.code}
            </div>
            {visibleCPColumns.map((col) => {
              const isOverridden =
                cpOverride[col.settingField] !== undefined;
              // Pending when the current value differs from the baseline
              // snapshot. On New trades (no baseline) this collapses to
              // isOverridden so orange still means "unsent change".
              const isPending = editBaseline
                ? !isSameOverrideFieldValue(
                    cpOverride[col.settingField],
                    cpBaseline?.[col.settingField],
                  )
                : isOverridden;
              // No explicit override → this cell follows the trade-level
              // default. If that default is itself pending, show muted
              // orange (updating with the default, but still linked).
              const isFollowingPending =
                !isOverridden &&
                isTradeLevelFieldPending(
                  thisTradeSettings,
                  tradeLevelBaseline,
                  col.settingField,
                );

              // LQC-1704: lock this CP's Advise Cover / Cover Spread dropdown
              // when a single CP is active.
              const lockedBySingleCp =
                !!lockCoverBySingleCp &&
                (col.settingField === "releaseCover" ||
                  col.settingField === "releaseCoverSpread");
              const singleCpTooltipId = lockedBySingleCp
                ? `coverSingleCpCp-${esid}-${cp.code}`
                : undefined;

              return (
                <div key={col.id} className={col.columnWidth}>
                  {(col.isCPCellVisible?.(cp.code) ?? true) &&
                    (col.type === "dropdown" ? (
                      <SettingDropdown
                        settingArray={
                          resolveSettingForCP(
                            thisTradeSettings,
                            cp.code,
                            col.settingField,
                          ) as
                            | IToggleButton[]
                            | IToggleButtonToReturnBoolean[]
                        }
                        esid={esid}
                        settingField={col.settingField}
                        cpCode={cp.code}
                        fontSizeClass={fontSizeClass}
                        disabled={col.disabled || lockedBySingleCp}
                        isOverridden={isOverridden}
                        isPending={isPending}
                        isFollowingPending={isFollowingPending}
                        tooltipId={singleCpTooltipId}
                        tooltipMessage={
                          lockedBySingleCp ? SINGLE_CP_COVER_TOOLTIP : undefined
                        }
                      />
                    ) : (
                      (() => {
                        const cpValue = resolveSettingForCP(
                          thisTradeSettings,
                          cp.code,
                          col.settingField,
                        ) as number | undefined;
                        // LQC-1040: floor for per-CP iceberg edits
                        // is the highest value the bank has seen
                        // — explicit baseline if present, otherwise
                        // the trade-level default (the value the
                        // CP received via fallback at last send).
                        // CPs added during the edit session that
                        // weren't in the GET have no floor.
                        const isIceberg = col.settingField === "releaseSize";
                        const isApiKnownCp = (knownCpCodes ?? []).includes(
                          cp.code,
                        );
                        const floor = isIceberg
                          ? getEffectiveIcebergFloorForCP(
                              editBaseline,
                              cp.code,
                              isApiKnownCp,
                            )
                          : undefined;
                        // Grey out when there's no room to move:
                        // current is at the cap and the floor is
                        // also the cap (so reduction is forbidden).
                        // A CP whose explicit baseline is below 100
                        // can still come back down to that baseline
                        // even when staged at 100.
                        const cpGreyedOut =
                          isIceberg &&
                          Number(cpValue) === 100 &&
                          floor === 100;
                        // Show "Must be X% or above" only when
                        // there's room to reduce (floor below cap).
                        const showFloorTooltip =
                          isIceberg &&
                          !cpGreyedOut &&
                          floor !== undefined &&
                          floor < 100;
                        let tooltipId: string | undefined;
                        let tooltipMessage: string | undefined;
                        if (cpGreyedOut) {
                          tooltipId = `cpIcebergLocked-${esid}-${cp.code}`;
                          tooltipMessage = ICEBERG_AT_MAX_TOOLTIP;
                        } else if (showFloorTooltip) {
                          tooltipId = `cpIcebergFloor-${esid}-${cp.code}`;
                          tooltipMessage = `Must be ${floor}% or above`;
                        }
                        return (
                          <SettingNumericInput
                            value={cpValue}
                            esid={esid}
                            settingField={col.settingField}
                            cpCode={cp.code}
                            fontSizeClass={fontSizeClass}
                            min={col.min}
                            max={col.max}
                            suffix={col.suffix}
                            isOverridden={isOverridden}
                            isPending={isPending}
                            isFollowingPending={isFollowingPending}
                            disabled={cpGreyedOut}
                            tooltipId={tooltipId}
                            tooltipMessage={tooltipMessage}
                          />
                        );
                      })()
                    ))}
                </div>
              );
            })}
            <div className={`${removeWidth} flex items-center gap-1`}>
              {hasAnyOverride && (
                <>
                  <button
                    data-tooltip-id={`cp-reset-${cp.code}`}
                    className={`${fontSizeClass} pl-1 text-gray-400 hover:text-blue-500 dark:hover:text-blue-400`}
                    onClick={() => {
                      resetCPOverrideFields({
                        esid,
                        cpCode: cp.code,
                      });
                    }}
                  >
                    {refreshArrow}
                  </button>
                  <Tooltip
                    id={`cp-reset-${cp.code}`}
                    place="top"
                    delayShow={100}
                    className="z-50 !text-xs"
                  >
                    Reset to defaults
                  </Tooltip>
                </>
              )}
            </div>
          </div>
        );
      })}
    </>
  );
}

export default CPOverrideRows;
